/**
 * F11.72: modal para crear una SEGUNDA (o N) empresa.
 * Flujo: RUC → lookup SUNAT → validar GG/titular → crear.
 */
import { useState } from "react"
import { createPortal } from "react-dom"
import { motion } from "framer-motion"
import { X, Search, Loader2, CheckCircle2, XCircle, Building2, User, AlertTriangle } from "lucide-react"
import { api } from "../../../api/client"

export default function CreateCompanyModal({ open, onClose, onCreated, createCompany }) {
  const [ruc, setRuc] = useState("")
  const [razonSocial, setRazonSocial] = useState("")
  const [lookupLoading, setLookupLoading] = useState(false)
  const [lookupResult, setLookupResult] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  if (!open) return null

  function reset() {
    setRuc("")
    setRazonSocial("")
    setLookupResult(null)
    setError(null)
  }

  function close() {
    if (saving) return
    reset()
    onClose()
  }

  async function handleLookup() {
    if (!ruc || ruc.length !== 11) return
    setLookupLoading(true)
    setLookupResult(null)
    setError(null)
    try {
      const r = await api.lookupRuc(ruc)
      setLookupResult(r)
      if (r.razonSocial) setRazonSocial(r.razonSocial)
    } catch (e) {
      setLookupResult({ ok: false, error: e?.message || "Error consultando SUNAT" })
    } finally {
      setLookupLoading(false)
    }
  }

  async function handleCreate() {
    if (!lookupResult || !lookupResult.ok || !lookupResult.razonSocial) return
    setSaving(true)
    setError(null)
    try {
      // FIX: Usamos la funcion del AuthContext para actualizar el estado global
      const r = await createCompany({
        ruc,
        razonSocial: razonSocial || lookupResult.razonSocial,
      })
      if (r && r.ok) {
        if (onCreated) onCreated(r)
        reset()
        onClose()
      } else {
        setError(r?.error || "Error creando empresa")
      }
    } catch (e) {
      setError(e?.message || "Error creando empresa")
    } finally {
      setSaving(false)
    }
  }

  const esNatural = lookupResult?.tipoRuc === "10"
  const rolLabel = esNatural ? "Titular" : "Gerente General"
  const IconoRol = esNatural ? User : Building2
  const valido = lookupResult?.ok && lookupResult?.valido

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && close()}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="bg-brand-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            <h3 className="font-bold text-lg">Crear nueva empresa</h3>
          </div>
          <button onClick={close} disabled={saving} className="p-1 hover:bg-white/20 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-xs text-surface-500">
            Validá el RUC en SUNAT. Solo el Gerente General (RUC 20) o el Titular (RUC 10) puede crear la empresa.
          </p>

          <div>
            <label className="text-xs font-semibold text-surface-700 block mb-1">RUC</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={ruc}
                onChange={(e) => setRuc(e.target.value.replace(/\D/g, "").slice(0, 11))}
                placeholder="20123456789"
                maxLength={11}
                disabled={saving || lookupLoading}
                className="input flex-1"
              />
              <button
                type="button"
                onClick={handleLookup}
                disabled={ruc.length !== 11 || lookupLoading || saving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 text-white text-xs font-semibold hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              >
                {lookupLoading ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando...</>
                ) : (
                  <><Search className="w-3.5 h-3.5" /> Validar</>
                )}
              </button>
            </div>
          </div>

          {lookupResult && lookupResult.ok === false && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">{lookupResult.code || "Error"}</div>
                <div>{lookupResult.error}</div>
              </div>
            </div>
          )}

          {lookupResult && lookupResult.ok && (
            <div className="text-xs bg-green-50 border border-green-200 rounded-lg p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 text-green-800 font-semibold">
                <CheckCircle2 className="w-4 h-4" />
                {lookupResult.razonSocial}
              </div>
              <div className="flex items-center gap-1.5 text-surface-600">
                <IconoRol className="w-3.5 h-3.5" />
                {esNatural
                  ? `Titular: ${lookupResult.titularNombre} (DNI ${lookupResult.titularDni})`
                  : `Verificado como ${rolLabel}`}
              </div>
              <div className="text-surface-500">
                RUC {lookupResult.tipoRuc === "10" ? "persona natural" : "persona jurídica"} · {lookupResult.estado || "—"} · {lookupResult.condicion || "—"}
              </div>
              {!lookupResult.isApiEnabled && (lookupResult.motivosRechazo || []).length > 0 && (
                <div className="text-amber-700 text-[11px] pt-1 border-t border-green-200">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  {lookupResult.motivosRechazo.join(" · ")}
                </div>
              )}
            </div>
          )}

          {lookupResult?.ok && (
            <div>
              <label className="text-xs font-semibold text-surface-700 block mb-1">Razón social</label>
              <input
                type="text"
                value={razonSocial}
                onChange={(e) => setRazonSocial(e.target.value)}
                disabled={saving}
                className="input w-full"
              />
            </div>
          )}

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              onClick={close}
              disabled={saving}
              className="flex-1 px-4 py-2 rounded-lg border border-surface-300 text-surface-700 text-sm font-semibold hover:bg-surface-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreate}
              disabled={saving || !valido || !razonSocial}
              className="flex-1 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Creando..." : "Crear empresa"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>,
    document.body
  )
}
